import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FlowguardLogo } from '../components/FlowguardLogo';
import { Building2, CheckCircle } from 'lucide-react';
import { useAuth } from '@/App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Onboarding() {
  const navigate = useNavigate();
  const { updatePreferences } = useAuth();
  const [frameworks, setFrameworks] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFrameworks();
  }, []);

  const fetchFrameworks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/frameworks`);
      const data = await res.json();
      setFrameworks(data.frameworks || []);
      // Default to first framework
      if (data.frameworks?.length > 0) {
        setSelectedCountry(data.frameworks[0].country_code);
      }
    } catch (err) {
      console.error('Failed to fetch frameworks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!selectedCountry) return;
    
    setSaving(true);
    try {
      await updatePreferences({ default_country: selectedCountry });
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to save preferences:', err);
      // Still navigate even if save fails
      navigate('/dashboard');
    } finally {
      setSaving(false);
    }
  };

  const countryFeatures = {
    CA: [
      'CMHC MLI Select (up to 50yr amortization)',
      'Standard Bank Financing',
      'CAD Currency',
      'Capital Cost Allowance (CCA)'
    ],
    US: [
      'Fannie Mae / Freddie Mac',
      'FHA 221(d)(4) & 223(f)',
      'USD Currency',
      'MACRS Depreciation'
    ]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/dashboard" className="flex items-center justify-center mb-4">
            <FlowguardLogo size="xl" />
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Manrope'] mb-4">
            Welcome! Let's personalize your experience
          </h1>
          <p className="text-slate-400 text-lg">
            Where do you primarily invest? This will customize your financing options and calculations.
          </p>
        </div>

        {/* Country Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {frameworks.map((fw) => (
            <div
              key={fw.country_code}
              onClick={() => setSelectedCountry(fw.country_code)}
              className={`relative cursor-pointer rounded-2xl p-6 transition-all duration-300 ${
                selectedCountry === fw.country_code
                  ? 'bg-gradient-to-b from-amber-500/10 to-slate-900 border-2 border-amber-500 scale-[1.02]'
                  : 'bg-slate-900/50 border border-slate-800 hover:border-slate-600'
              }`}
            >
              {selectedCountry === fw.country_code && (
                <div className="absolute top-4 right-4">
                  <CheckCircle className="h-6 w-6 text-amber-500" />
                </div>
              )}
              
              <div className="text-5xl mb-4">{fw.flag_emoji}</div>
              <h3 className="text-xl font-bold text-white font-['Manrope'] mb-2">
                {fw.country_name}
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                {fw.product_count} financing products available
              </p>
              
              <ul className="space-y-2">
                {(countryFeatures[fw.country_code] || []).map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Continue Button */}
        <div className="text-center">
          <Button
            onClick={handleContinue}
            disabled={!selectedCountry || saving}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8 py-3 text-lg"
          >
            {saving ? 'Saving...' : `Continue with ${selectedCountry === 'CA' ? 'Canada' : 'United States'}`}
          </Button>
          
          <p className="text-slate-500 text-sm mt-4">
            You can change this anytime in Settings. You can also analyze deals in other countries.
          </p>
        </div>
      </div>
    </div>
  );
}
